import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PageHeader } from '@carrent/ui'
import { DataTable } from '../../components/DataTable'
import { StatusBadge } from '@carrent/ui'
import { adminApi } from '@carrent/api'

const FILTERS = [
  { label: 'Все',         value: '',         icon: '👥' },
  { label: 'На проверке', value: 'pending',  icon: '⏳' },
  { label: 'Активные',    value: 'approved', icon: '✅' },
  { label: 'Черновики',   value: 'draft',    icon: '📝' },
  { label: 'Отклонённые', value: 'rejected', icon: '❌' },
  { label: 'Заблок.',     value: 'blocked',  icon: '🚫' },
]

const DOC_LABELS = ['Паспорт', 'Прописка', 'Селфи', 'Селфи+паспорт']

const USER_COLUMNS = [
  {
    key: 'full_name', label: 'ФИО', sortable: true,
    render: u => <span className="table__title">{u.full_name}</span>,
  },
  { key: 'phone', label: 'Телефон', cellClassName: 'table__muted' },
  {
    key: 'email', label: 'Email', cellClassName: 'table__muted',
    render: u => u.email || '—',
  },
  {
    key: 'status', label: 'Статус', width: 'status',
    render: u => <StatusBadge status={u.status} />,
  },
  {
    key: 'docs', label: 'Документы',
    render: u => (
      <div className="doc-dots">
        {[u.has_passport_main, u.has_passport_address, u.has_selfie, u.has_selfie_passport]
          .map((done, i) => (
            <span key={i} title={DOC_LABELS[i]}
              className={`doc-dots__item ${done ? 'doc-dots__item--done' : ''}`.trim()} />
          ))}
      </div>
    ),
  },
  {
    key: 'created_at', label: 'Регистрация', sortable: true, cellClassName: 'table__muted',
    render: u => new Date(u.created_at).toLocaleDateString('ru', { dateStyle: 'medium' }),
  },
]

export default function AdminUsersPage() {
  const [users, setUsers]           = useState([])
  const [counts, setCounts]         = useState({})
  const [loading, setLoading]       = useState(true)
  const [search, setSearch]         = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const status = searchParams.get('status') || ''

  const load = useCallback(() => {
    setLoading(true)
    adminApi.listUsers({ status: status || undefined, q: search || undefined, per_page: 50 })
      .then(r => setUsers(r.data?.data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [status, search])

  // Загружаем счётчики по каждому статусу
  useEffect(() => {
    Promise.all(
      ['pending', 'approved', 'blocked', 'draft', 'rejected'].map(s =>
        adminApi.listUsers({ status: s, per_page: 1 })
          .then(r => [s, r.data?.data?.meta?.total || 0])
          .catch(() => [s, 0])
      )
    ).then(pairs => setCounts(Object.fromEntries(pairs)))
  }, [])

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0)
    return () => clearTimeout(t)
  }, [load])

  return (
    <div>
      <PageHeader title="Водители" />

      {/* Filter tabs */}
      <div style={{ padding: '0 var(--gap)', paddingBottom: 'var(--gap-sm)' }}>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2, scrollbarWidth: 'none' }}>
          {FILTERS.map(f => {
            const count = counts[f.value]
            const isActive = status === f.value
            return (
              <button key={f.value}
                className={`btn ${isActive ? 'btn--primary' : 'btn--outline'}`}
                style={{ flexShrink: 0, padding: '6px 12px', fontSize: '0.82rem',
                  display: 'flex', alignItems: 'center', gap: 4 }}
                onClick={() => setSearchParams(f.value ? { status: f.value } : {})}>
                {f.icon} {f.label}
                {count > 0 && (
                  <span style={{
                    background: isActive ? 'rgba(255,255,255,0.25)' : 'var(--border)',
                    color: isActive ? '#fff' : 'var(--text-muted)',
                    fontSize: '0.72rem', padding: '1px 6px', borderRadius: 99, fontWeight: 700,
                  }}>{count}</span>
                )}
              </button>
            )
          })}
        </div>

        {/* Search */}
        <div style={{ position: 'relative', marginTop: 'var(--gap-sm)' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            fontSize: '1rem', pointerEvents: 'none' }}>🔍</span>
          <input className="input" style={{ paddingLeft: 36 }}
            placeholder="Поиск по имени или телефону..."
            value={search} onChange={e => setSearch(e.target.value)} />
          {search && (
            <button onClick={() => setSearch('')}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
                fontSize: '1rem' }}>✕</button>
          )}
        </div>
      </div>

      {/* User list */}
      <div className="p-page flex-col" style={{ gap: 'var(--gap-sm)', paddingTop: 0 }}>
        <DataTable
          columns={USER_COLUMNS}
          rows={users}
          loading={loading}
          onRowClick={u => navigate(`/admin/users/${u.id}`)}
          empty={{ icon: 'users', title: 'Пользователей не найдено',
            text: 'Измените фильтры или поисковый запрос.' }}
        />
      </div>
    </div>
  )
}
